import { useCallback, useEffect, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Pencil, Plus, Trash2, UserRound } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { card } from "@/components/admin/SettingsPanel";
import { SortableArea } from "@/components/admin/Sortable";
import { PastorForm } from "@/components/admin/PastorForm";
import { UploadBtn } from "@/components/admin/SermonForm";

const pendingToast = () => toast("Sent for approval. It will appear once a Super Admin approves it.");

const Row = ({ p, writable, count, onEdit, onDelete, onAddFiles }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: p.id, disabled: !writable });
  return (
    <li ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} data-testid={`admin-pastor-${p.slug}`} className={`flex items-center gap-3 border-b border-[#F4EEE2]/10 py-3 ${isDragging ? "relative z-10 rounded-xl bg-[#131C2E] shadow-xl" : ""}`}>
      {writable && <button type="button" data-testid={`admin-pastor-drag-${p.slug}`} {...attributes} {...listeners} aria-label="Drag to reorder" className="cursor-grab touch-none rounded-lg p-1.5 text-[#B7BFCC] active:cursor-grabbing"><GripVertical className="h-4 w-4" /></button>}
      {p.photo_url ? <img src={fileUrl(p.photo_url)} alt="" className="h-11 w-11 rounded-full object-cover" /> : <span className="grid h-11 w-11 place-items-center rounded-full bg-[#1C2D42]"><UserRound className="h-5 w-5 text-[#B7BFCC]" /></span>}
      <div className="min-w-0 flex-1">
        <p className="truncate font-ui font-semibold text-[#F4EEE2]">{p.name}</p>
        <p className="truncate text-xs text-[#B7BFCC]">{p.role_en || "Pastor"} · {p.files?.length || 0} sermon file(s) · {count} message(s)</p>
      </div>
      <a href={`/pastors/${p.slug}`} target="_blank" rel="noreferrer" data-testid={`admin-pastor-view-${p.slug}`} className="hidden font-ui text-xs text-[#D4AF37] hover:underline sm:block">View page</a>
      {writable && <UploadBtn multiple library testid={`admin-pastor-addfiles-${p.slug}`} label="Add sermon files" onDone={(u, f) => onAddFiles(p, { url: u, name: f.name, type: f.type })} />}
      {writable && <button type="button" data-testid={`admin-pastor-edit-${p.slug}`} onClick={onEdit} className="rounded-full p-2 text-[#B7BFCC] hover:text-[#D4AF37]" aria-label="Edit"><Pencil className="h-4 w-4" /></button>}
      {writable && <button type="button" data-testid={`admin-pastor-delete-${p.slug}`} onClick={onDelete} className="rounded-full p-2 text-[#B7BFCC] hover:text-[#F2A08A]" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>}
    </li>
  );
};

// Pastor profiles, shown at the top of the Messages tab.
export const PastorsPanel = ({ writable }) => {
  const [list, setList] = useState([]);
  const [sermons, setSermons] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => {
    api.get("/pastors").then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch(() => setList([]));
    api.get("/sermons").then((r) => setSermons(Array.isArray(r.data) ? r.data : [])).catch(() => setSermons([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  const count = (p) => sermons.filter((s) => s.speaker?.trim().toLowerCase() === p.name.trim().toLowerCase()).length;
  const reorder = async (ids) => {
    const prev = list;
    setList(ids.map((id) => list.find((p) => p.id === id)));
    try { await api.put("/pastors/order", { ids }); toast.success("Order saved"); } catch (e) { setList(prev); toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addFiles = async (p, file) => {
    try {
      const cur = (await api.get("/pastors")).data.find((x) => x.id === p.id) || p;
      const { data } = await api.put(`/pastors/${p.id}`, { ...cur, files: [...(cur.files || []), file] });
      if (data?.pending) pendingToast(); else toast.success(`Added to ${p.name}'s card`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete ${p.name}'s profile? Their messages stay.`)) return;
    try { const { data } = await api.delete(`/pastors/${p.id}`); if (data?.pending) pendingToast(); else toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div data-testid="pastors-panel" className={`${card} mb-4`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-2xl text-[#F4EEE2]">Pastors</h3>
          <p className="mt-1 text-sm text-[#B7BFCC]">Add each pastor here: name, title, bio, photo and their own sermon files (PDF, Word, any format), all in one place. Each pastor's files stay on their own page and are never mixed with another pastor's. Messages whose “Pastor / Speaker” matches the name also show on their page.</p>
        </div>
        {writable && <button type="button" data-testid="add-pastor-btn" onClick={() => setEditing({})} className="flex items-center gap-2 rounded-full bg-[#D4AF37] px-5 py-2.5 font-ui text-sm font-semibold text-[#0B111E]"><Plus className="h-4 w-4" /> Add pastor</button>}
      </div>
      {list.length ? (
        <SortableArea ids={list.map((p) => p.id)} onReorder={reorder}>
          <ul className="mt-3">{list.map((p) => <Row key={p.id} p={p} writable={writable} count={count(p)} onEdit={() => setEditing(p)} onDelete={() => del(p)} onAddFiles={addFiles} />)}</ul>
        </SortableArea>
      ) : (
        <p data-testid="pastors-empty" className="mt-4 text-sm text-[#9AA4B4]">No pastors yet. Press “Add pastor”.</p>
      )}
      {editing && <PastorForm initial={editing} pastors={list} onClose={() => setEditing(null)} onSaved={(pending, merged) => { if (pending) pendingToast(); else toast.success(merged ? "This pastor already had a card. The files were added to it." : "Saved"); setEditing(null); load(); }} />}
    </div>
  );
};
